/**
 * Replay chunk transport for the Observ RUM SDK (FR-2/FR-5, story 2.2).
 *
 * The heavy rrweb flux travels on its OWN channel — `POST /v1/replay` — never
 * through the OTLP exporter (architecture: the two channels are never crossed).
 * The body is the raw gzip bytes of a `.jsonl.gz` chunk; the backend (story 2.1)
 * stores it verbatim and rejects an empty body (400), so we never POST one.
 *
 * Contract frozen by story 2.1:
 *   POST {endpoint}/v1/replay?session_id=<id>&seq=<n>
 *   headers: x-observ-key: <key>, content-type: application/octet-stream
 *   body: gzip bytes (≤ 16 MiB)
 */

/**
 * Join an endpoint base URL with the `/v1/replay` path. Mirror of
 * {@link import('./otel-rum.js').buildTracesUrl}: the `URL` API preserves a path
 * prefix, never doubles a trailing slash, and drops any query/fragment; a
 * relative or empty endpoint falls back to a same-origin path.
 */
export function buildReplayUrl(endpoint: string): string {
  try {
    const url = new URL(endpoint)
    url.pathname = `${url.pathname.replace(/\/+$/, '')}/v1/replay`
    url.search = ''
    url.hash = ''
    return url.toString()
  } catch {
    return `${endpoint.replace(/\/+$/, '')}/v1/replay`
  }
}

/**
 * Gzip newline-delimited JSON lines via the native `CompressionStream` — no
 * dependency (resolves the story 1.1 gzip-strategy deferral). Available in every
 * evergreen browser and Node ≥ 18. Uses the `Blob.stream()` → `pipeThrough` →
 * `Response.arrayBuffer()` pipeline so there is no manual writer/reader (no
 * unhandled rejection, no backpressure deadlock). The output is the gzip stream,
 * stored as-is by the backend.
 */
export async function gzipJsonl(lines: string[]): Promise<Uint8Array> {
  const data = new TextEncoder().encode(lines.join('\n'))
  const stream = new Blob([data as BlobPart]).stream().pipeThrough(new CompressionStream('gzip'))
  const buf = await new Response(stream).arrayBuffer()
  return new Uint8Array(buf)
}

/** Arguments for {@link postReplayChunk}. */
export interface PostReplayChunkOptions {
  /** Replay endpoint URL (from {@link buildReplayUrl}). */
  url: string
  /** API key for the `x-observ-key` header (omitted when empty). */
  key: string
  /** Current visit `session.id` (query param, keys the object-store prefix). */
  sessionId: string
  /** Monotonic chunk sequence within the session (query param). */
  seq: number
  /** Gzip bytes of the chunk. An empty body is never sent (server returns 400). */
  body: Uint8Array
  /** Use `fetch` keepalive for an unload flush (body capped ~64 KiB by the spec). */
  keepalive?: boolean
}

/**
 * POST one gzip replay chunk. **Best-effort: never throws** — a network failure
 * degrades to "this chunk is lost", it must not break the host page.
 */
export async function postReplayChunk(opts: PostReplayChunkOptions): Promise<void> {
  if (opts.body.length === 0) return // backend rejects an empty body (400)

  const headers: Record<string, string> = { 'content-type': 'application/octet-stream' }
  // Mirror otel-rum.ts: a blank key would be a malformed credential — omit it.
  if (opts.key) headers['x-observ-key'] = opts.key

  const url = `${opts.url}?session_id=${encodeURIComponent(opts.sessionId)}&seq=${opts.seq}`
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers,
      body: opts.body as BodyInit,
      keepalive: opts.keepalive,
    })
    // `fetch` only rejects on network failure — surface server rejections
    // (400 empty / 401 auth / 413 over the 16 MiB cap) instead of silent success.
    if (!res.ok) {
      console.warn(`[observ] replay chunk rejected: HTTP ${res.status}`)
    }
  } catch (err) {
    console.warn('[observ] replay chunk upload failed', err)
  }
}
